import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismIcon, PrismTooltip } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq } from 'ember-truth-helpers';
import type { Ota } from 'frontend-utils/models';
import hasFeature from 'lumen/helpers/has-feature';

interface GeneralButtonsLiveshopButtonSignature {
  Args: {
    hasLiveShop: boolean;
    isParity?: boolean;
    noDataForSelection?: {
      liveUpdateDisabled: boolean;
    };
    onClick: () => void;
    selectedOTA?: Ota | null;
  };
}

export default class GeneralButtonsLiveshopButton extends Component<GeneralButtonsLiveshopButtonSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare liveupdate: Services['liveupdate'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isActive() {
    return this.liveupdate?.isPolling;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDisabled() {
    let isDisabled = true;

    if (
      this.args.hasLiveShop &&
      (this.liveupdate?.parityTabOpen || this.otaSupported) &&
      !this.isMonthLiveupdateLimitReached &&
      !this.isPartialLiveupdateOngoing &&
      !this.args.noDataForSelection?.liveUpdateDisabled
    ) {
      isDisabled = false;
    }

    return isDisabled;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMonthLiveupdateLimitReached() {
    return this.liveupdate?.isMonthLiveupdateLimitReached;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPartialLiveupdateOngoing() {
    return this.liveupdate?.partialLiveupdatesForThisSelection?.length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otaSupported() {
    if (this.liveupdate?.parityTabOpen) {
      return true;
    }

    if (!this.args.selectedOTA) {
      return false;
    }

    const otaSupported = this.hotel?.canLiveShopForOTA(
      // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
      this.args.selectedOTA?.id,
      this.access.hasFeature('rates.liveshop.ctrip'),
    );

    return otaSupported;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showButtonAfterProductToPlanChanges() {
    /*
     BE can return 2 feature flags:
     - rates.liveshop.custom
     - rates.liveshop.custom.hide

    + Show if only rates.liveshop.custom is present
    + Show if both flags are present
    + Show button if none of the flags are present (pre product-to-plan change upsell behavior)
    - Hide if only rates.liveshop.custom.hide is present
    */
    return (
      this.access.hasFeature('rates.liveshop.custom') ||
      !this.access.hasFeature('rates.liveshop.custom.hide')
    );
  }

  @action click(): void {
    if (!this.isDisabled) {
      this.args.onClick();
    }
  }

  <template>
    {{#if this.showButtonAfterProductToPlanChanges}}
      <div data-test-id="liveshop-button">
        <button
          class="prism-button prism-button--primary flex items-center justify-center gap-100 text-nowrap"
          disabled={{this.isDisabled}}
          type="button"
          {{on "click" this.click}}
        >
          <PrismIcon
            @color="inherit"
            @name="sync"
            @size="small"
            class={{if this.isActive "spinning"}}
          />
          {{t "general.liveshop.refresh_rates"}}
        </button>
        {{#if @hasLiveShop}}
          {{#unless this.otaSupported}}
            {{#if this.isActive}}
              <PrismTooltip
                @side="left"
                @text={{t "rates.liveupdates.live_shop_in_progress"}}
              />
              {{! TEMP CTRIP code }}
            {{else if (eq @selectedOTA.id "ctrip")}}
              {{! t "rates.liveupdates.platform_not_supported"}}
              <PrismTooltip
                @side="left"
                @text={{t "rates.liveupdates.ctrip_not_supported"}}
              />
            {{else}}
              <PrismTooltip
                @side="left"
                @text={{t "rates.liveupdates.ota_not_supported"}}
              />
            {{/if}}
          {{/unless}}
          {{#if this.isMonthLiveupdateLimitReached}}
            {{#if (and (hasFeature "app.limited_parity") @isParity)}}
              <PrismTooltip
                @side="left"
                @text={{t "error.liveupdate.max_limited_parity"}}
              />
            {{else}}
              <PrismTooltip
                @side="left"
                @text={{t "error.liveupdate.max_limit_for_hotel_and_ota"}}
              />
            {{/if}}
          {{/if}}
          {{#if this.isPartialLiveupdateOngoing}}
            <PrismTooltip
              @side="left"
              @text={{t "error.liveupdate.partial_liveshop_ongoing"}}
            />
          {{/if}}
        {{else}}
          <PrismTooltip
            @side="left"
            @text={{t "error.liveupdate.not_allowed_for_hotel"}}
          />
        {{/if}}
      </div>
    {{/if}}
  </template>
}
